// src/pages/Auth/ResetPassword.tsx - UPDATED with validation
import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../../api';
import { Lock, Loader2, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/input';
import toast from 'react-hot-toast';

const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong'>('weak');
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  const checkPasswordStrength = (pwd: string) => {
    const checks = {
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /[0-9]/.test(pwd),
      special: /[!@#$%^&*]/.test(pwd)
    };
    setPasswordChecks(checks);

    const strengthCount = Object.values(checks).filter(Boolean).length;
    if (strengthCount >= 5) setPasswordStrength('strong');
    else if (strengthCount >= 3) setPasswordStrength('medium');
    else setPasswordStrength('weak');
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const pwd = e.target.value;
    setPassword(pwd);
    checkPasswordStrength(pwd);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password || !confirm) {
      toast.error('Please fill all fields');
      return;
    }
    
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (passwordStrength === 'weak') {
      if (!window.confirm('This password is weak. Continue anyway?')) {
        return;
      }
    }

    setLoading(true);
    try {
      const res = await authAPI.resetPassword({ token: token!, password });
      if (res.data.success) {
        toast.success('Password reset successful! Please login.');
        navigate('/login');
      } else {
        toast.error(res.data.message || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Set New Password</h1>
        <p className="text-gray-600 mb-6">Enter your new password below.</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={handlePasswordChange}
              placeholder="New password"
              className="pl-10 pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              {showPassword ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
            </button>
          </div>

          {/* Password strength indicators */}
          {password && (
            <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${
                      passwordStrength === 'strong' ? 'bg-green-500 w-full' :
                      passwordStrength === 'medium' ? 'bg-yellow-500 w-2/3' :
                      'bg-red-500 w-1/3'
                    }`}
                  />
                </div>
                <span className={`text-xs font-medium ${
                  passwordStrength === 'strong' ? 'text-green-600' :
                  passwordStrength === 'medium' ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {passwordStrength}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1">
                  {passwordChecks.length ? 
                    <CheckCircle className="h-3 w-3 text-green-500" /> : 
                    <XCircle className="h-3 w-3 text-red-500" />
                  }
                  <span>8+ characters</span>
                </div>
                <div className="flex items-center gap-1">
                  {passwordChecks.uppercase ? 
                    <CheckCircle className="h-3 w-3 text-green-500" /> : 
                    <XCircle className="h-3 w-3 text-red-500" />
                  }
                  <span>Uppercase</span>
                </div>
                <div className="flex items-center gap-1">
                  {passwordChecks.lowercase ? 
                    <CheckCircle className="h-3 w-3 text-green-500" /> : 
                    <XCircle className="h-3 w-3 text-red-500" />
                  }
                  <span>Lowercase</span>
                </div>
                <div className="flex items-center gap-1">
                  {passwordChecks.number ? 
                    <CheckCircle className="h-3 w-3 text-green-500" /> : 
                    <XCircle className="h-3 w-3 text-red-500" />
                  }
                  <span>Number</span>
                </div>
                <div className="flex items-center gap-1 col-span-2">
                  {passwordChecks.special ? 
                    <CheckCircle className="h-3 w-3 text-green-500" /> : 
                    <XCircle className="h-3 w-3 text-red-500" />
                  }
                  <span>Special character (!@#$%^&*)</span>
                </div>
              </div>
            </div>
          )}

          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type={showConfirm ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              className="pl-10 pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              {showConfirm ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
            </button>
          </div>
          {confirm && password !== confirm && (
            <p className="text-xs text-red-500">Passwords do not match</p>
          )}

          <Button type="submit" disabled={loading} className="w-full">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Reset Password'}
          </Button>
        </form>
        <div className="mt-4 text-center">
          <Link to="/login" className="text-emerald-600 hover:text-emerald-700 text-sm">
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;